// AdminAssessmentPanel — Assessment Management tab in Admin Dashboard.
// Two sub-sections:
//   1. Requests — pending/approved/rejected requests from HR users
//   2. Library  — all custom assessment types per company with enable/disable

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  CheckCircle, XCircle, Clock, Building2, RefreshCw,
  ToggleLeft, ToggleRight, Edit2, ChevronDown, ChevronUp,
} from 'lucide-react';
import api from '../../services/api';

const STATUS_BADGE = {
  pending:  { label: 'Pending',  cls: 'bg-yellow-100 text-yellow-700', Icon: Clock },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700',   Icon: CheckCircle },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700',       Icon: XCircle },
};

// ── Requests sub-panel ───────────────────────────────────────────────────────
function RequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [reviewing, setReviewing] = useState(null); // requestId being actioned
  const [adminNote, setAdminNote] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [filter, setFilter] = useState('all');

  const load = async () => {
    try { setLoading(true);
      const r = await api.get('/admin/assessment-requests');
      setRequests(r.data?.data || []);
    } catch { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleReview = async (id, status) => {
    try {
      setReviewing(id);
      await api.put(`/admin/assessment-requests/${id}/review`, {
        status, adminNote,
        label: editLabel || undefined,
      });
      toast.success(status === 'approved' ? 'Request approved — type added to workspace' : 'Request rejected');
      setExpanded(null);
      setAdminNote('');
      setEditLabel('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to review request');
    } finally { setReviewing(null); }
  };

  const displayed = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center gap-2">
        {['all','pending','approved','rejected'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
              filter === f ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {f} {f === 'pending' && requests.filter(r => r.status === 'pending').length > 0 &&
              <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {requests.filter(r => r.status === 'pending').length}
              </span>}
          </button>
        ))}
        <button onClick={load} className="ml-auto p-2 hover:bg-gray-100 rounded-full">
          <RefreshCw className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {displayed.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No requests found</div>
      ) : displayed.map(req => {
        const badge = STATUS_BADGE[req.status];
        const BadgeIcon = badge.Icon;
        const isOpen = expanded === req._id;
        return (
          <div key={req._id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="p-4 flex items-start justify-between gap-4 cursor-pointer"
              onClick={() => setExpanded(isOpen ? null : req._id)}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900">{req.name}</h3>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>
                    <BadgeIcon className="w-3 h-3" />{badge.label}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1.5">
                  <Building2 className="w-3 h-3" />
                  {req.companyName || req.companyCode} &bull; {req.requestedBy?.name || req.requestedBy?.email}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{new Date(req.createdAt).toLocaleDateString()}</p>
              </div>
              {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />}
            </div>

            {isOpen && (
              <div className="border-t border-gray-100 p-4 space-y-3 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div><span className="font-medium text-gray-600">Description:</span><p className="text-gray-700 mt-0.5">{req.description}</p></div>
                  <div><span className="font-medium text-gray-600">Purpose:</span><p className="text-gray-700 mt-0.5">{req.purpose}</p></div>
                  <div className="md:col-span-2"><span className="font-medium text-gray-600">Evaluation Method:</span><p className="text-gray-700 mt-0.5">{req.evaluationMethod}</p></div>
                  {req.adminNote && <div className="md:col-span-2"><span className="font-medium text-gray-600">Admin Note:</span><p className="text-gray-700 mt-0.5">{req.adminNote}</p></div>}
                </div>

                {req.status === 'pending' && (
                  <div className="border-t border-gray-200 pt-3 space-y-2">
                    <div>
                      <label className="text-xs font-medium text-gray-600">Display Label (optional — edit before approving)</label>
                      <input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                        placeholder={req.name}
                        className="mt-1 w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600">Admin Note (optional)</label>
                      <textarea value={adminNote} onChange={e => setAdminNote(e.target.value)} rows={2}
                        className="mt-1 w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none" />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => handleReview(req._id, 'approved')}
                        disabled={reviewing === req._id}
                        className="flex items-center gap-1.5 px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50">
                        <CheckCircle className="w-4 h-4" />
                        {reviewing === req._id ? 'Processing…' : 'Approve'}
                      </button>
                      <button onClick={() => handleReview(req._id, 'rejected')}
                        disabled={reviewing === req._id}
                        className="flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50">
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Library sub-panel ────────────────────────────────────────────────────────
function LibraryPanel() {
  const [types, setTypes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', description: '' });

  const load = async () => {
    try { setLoading(true);
      const r = await api.get('/admin/assessment-types');
      setTypes(r.data?.data || []);
    } catch { toast.error('Failed to load assessment types'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleEnabled = async (type) => {
    try {
      await api.put(`/admin/assessment-types/${type._id}`, { enabled: !type.enabled });
      toast.success(`${type.label} ${!type.enabled ? 'enabled' : 'disabled'}`);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/admin/assessment-types/${id}`, editForm);
      toast.success('Updated');
      setEditing(null);
      load();
    } catch { toast.error('Failed to save'); }
  };

  // Group by company
  const grouped = types.reduce((acc, t) => {
    const key = t.companyCode;
    if (!acc[key]) acc[key] = { companyCode: key, companyName: t.companyName, types: [] };
    acc[key].types.push(t);
    return acc;
  }, {});

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  if (Object.keys(grouped).length === 0) return (
    <div className="text-center py-12 text-gray-400">No custom assessment types yet. Approve requests to add them.</div>
  );

  return (
    <div className="space-y-6">
      {Object.values(grouped).map(group => (
        <div key={group.companyCode} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-orange-500" />
            <span className="font-semibold text-gray-800">{group.companyName || group.companyCode}</span>
            <span className="text-xs text-gray-400 ml-1">({group.companyCode})</span>
            <span className="ml-auto text-xs text-gray-400">{group.types.length} type{group.types.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="divide-y divide-gray-100">
            {group.types.map(t => (
              <div key={t._id} className="p-4 flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  {editing === t._id ? (
                    <div className="space-y-2">
                      <input value={editForm.label} onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                      <input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Description"
                        className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                      <div className="flex gap-2">
                        <button onClick={() => saveEdit(t._id)} className="px-3 py-1 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600">Save</button>
                        <button onClick={() => setEditing(null)} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className={`font-medium ${t.enabled ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{t.label}</p>
                      <p className="text-sm text-gray-500 mt-0.5">{t.description || t.purpose}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Type slug: <code className="bg-gray-100 px-1 rounded">{t.type}</code></p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => { setEditing(t._id); setEditForm({ label: t.label, description: t.description }); }}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" title="Edit">
                    <Edit2 className="w-4 h-4 text-gray-400" />
                  </button>
                  <button onClick={() => toggleEnabled(t)} title={t.enabled ? 'Disable' : 'Enable'}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                    {t.enabled
                      ? <ToggleRight className="w-5 h-5 text-green-500" />
                      : <ToggleLeft  className="w-5 h-5 text-gray-400" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main panel ───────────────────────────────────────────────────────────────
export default function AdminAssessmentPanel() {
  const [sub, setSub] = useState('requests');
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Assessment Management</h2>
          <p className="text-sm text-gray-500 mt-0.5">Review requests and manage per-tenant assessment libraries</p>
        </div>
      </div>
      <div className="flex gap-2 border-b border-gray-200">
        {[['requests','Requests'], ['library','Assessment Library']].map(([key, label]) => (
          <button key={key} onClick={() => setSub(key)}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              sub === key ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {label}
          </button>
        ))}
      </div>
      {sub === 'requests' && <RequestsPanel />}
      {sub === 'library'  && <LibraryPanel />}
    </div>
  );
}
